import { ColumnConfig, PieConfig } from "@ant-design/plots";

import Strings from "utils/strings";
import { translate } from "utils/utils";

/**
 * Pure chart configuration of the dashboard: the series built from the API payload and the options of
 * the `<Column>` / `<Pie>` charts rendered on screen.
 */

export interface SeriesPoint {
	type: string;
	value: number;
}

export interface PieSeriesPoint extends SeriesPoint {
	percentage: number;
}

export const buildConsultationsSeries = (
	consultations?: { scheduled?: number; finished?: number; patients?: number } | null
): SeriesPoint[] => [
	{ type: Strings.dashboard.scheduled, value: consultations?.scheduled || 0 },
	{ type: Strings.dashboard.finished, value: consultations?.finished || 0 },
	{ type: Strings.sidebar.coworkers, value: consultations?.patients || 0 }
];

export const buildDiagnosisSeries = (diagnosis: Array<{ diagnostic: unknown; total: number }> = []): SeriesPoint[] =>
	diagnosis.map((diagnostic) => ({
		type: translate(diagnostic.diagnostic),
		value: diagnostic.total || 0
	}));

export const withPercentages = (data: SeriesPoint[]): PieSeriesPoint[] => {
	const total = data.reduce((sum, item) => sum + (item.value || 0), 0);

	return data.map((item) => ({
		...item,
		percentage: total > 0 ? (item.value / total) * 100 : 0
	}));
};

export const formatPercentage = (percentage: number) =>
	`${percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1)}%`;

/** True when there is at least one non-zero value to draw. */
export const hasChartData = (data: SeriesPoint[] = []) => data.some((item) => (item.value || 0) > 0);

export const buildColumnConfig = (data: SeriesPoint[]): ColumnConfig => ({
	data,
	xField: "type",
	yField: "value",
	seriesField: "type",
	dodgePadding: 2,
	columnWidthRatio: 0.3,
	label: {
		position: "middle",
		layout: [{ type: "interval-adjust-position" }, { type: "interval-hide-overlap" }, { type: "adjust-color" }]
	},
	legend: {
		position: "top-left"
	}
});

export const buildPieConfig = (data: PieSeriesPoint[]): PieConfig => ({
	appendPadding: 10,
	data,
	angleField: "value",
	colorField: "type",
	radius: 0.9,
	label: {
		type: "inner",
		offset: "-30%",
		content: (datum: { percentage?: number }) => formatPercentage(datum.percentage || 0),
		style: {
			fontSize: 14,
			textAlign: "center"
		}
	},
	interactions: [{ type: "element-active" }],
	legend: {
		position: "top-left"
	}
});
